/**
 * Role-based landing page (P0-1, UX-REVIEW §3.1).
 *
 * Tech view: one hero "Create a deliverable" action + a list of recent deliverables
 * with one-click re-download. Nothing else.
 * Admin view: additionally shows quick-link cards into the lab's setup surfaces
 * (Formats, Standards, Team, Connector).
 *
 * Reuses `useGenerationJobs` + `downloadJobBundle`; no new API surface.
 */
import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { useCurrentLocation } from "../locations/LocationContext";
import { useConfigs } from "../api/configs";
import { downloadJobBundle, useGenerationJobs } from "../api/generation";
import { errorMessage } from "../api/client";
import type { GenerationJob } from "../api/types";
import { Button } from "../components/Button";
import { Badge } from "../components/Badge";
import { LoadingBlock } from "../components/Spinner";
import { EmptyState, ErrorState } from "../components/EmptyState";
import { useToast } from "../components/Toast";
import { formatAbsolute, formatRelative } from "../utils/time";
import { statusTone } from "../utils/status";
import { useIsAdmin } from "../locations/OrgContext";

function RecentDeliverables() {
  const { t } = useTranslation();
  const toast = useToast();
  const { data, isLoading, isError, error, refetch } = useGenerationJobs({ limit: 8 });
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const download = async (job: GenerationJob) => {
    setDownloadingId(job.id);
    try {
      await downloadJobBundle(job.id, job.name);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setDownloadingId(null);
    }
  };

  if (isLoading) return <LoadingBlock label={t("common.loading")} />;

  // A failed fetch must not silently fall through to the "no deliverables yet" empty state.
  if (isError) {
    return (
      <ErrorState
        message={errorMessage(error)}
        action={<Button variant="ghost" onClick={() => void refetch()}>{t("home.recentError")}</Button>}
      />
    );
  }

  const jobs = data ?? [];
  if (jobs.length === 0) {
    return (
      <EmptyState
        title={t("home.recentEmptyTitle")}
        action={
          <Link to="/new" className="btn btn--primary">
            {t("home.createCta")}
          </Link>
        }
      >
        {t("home.recentEmptyBody")}
      </EmptyState>
    );
  }

  return (
    <div className="stack" style={{ gap: "var(--space-2)" }}>
      {jobs.map((job) => (
        <div key={job.id} className="card card--pad-sm row row--between row--wrap">
          <div className="row" style={{ gap: "var(--space-3)" }}>
            <Link to={`/generate/jobs/${job.id}`} style={{ fontWeight: 600 }}>
              {job.name || t("jobs.unnamed")}
            </Link>
            <Badge tone={statusTone(job.status)}>{job.status}</Badge>
            <span className="faint" title={formatAbsolute(job.created_at)}>
              {formatRelative(job.created_at)}
            </span>
          </div>
          {job.has_result ? (
            <Button
              size="sm"
              variant="ghost"
              loading={downloadingId === job.id}
              onClick={() => void download(job)}
            >
              {t("home.reDownload")}
            </Button>
          ) : (
            <span className="faint">{t("home.noResultYet")}</span>
          )}
        </div>
      ))}
      <Link to="/generate/jobs" className="btn btn--ghost btn--sm" style={{ alignSelf: "flex-start" }}>
        {t("home.viewAll")}
      </Link>
    </div>
  );
}

function AdminCard({
  to,
  title,
  body,
}: {
  to: string;
  title: string;
  body: string;
}) {
  return (
    <Link to={to} className="tpl-card">
      <span className="tpl-card__title">{title}</span>
      <span className="faint">{body}</span>
    </Link>
  );
}

export function HomePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const name = user?.display_name || user?.email?.split("@")[0] || "";

  // Configs scoped to the user's current location: a location is "un-onboarded"
  // when it has 0 of its own formats (Phase 6.2 first-run detection).
  const { currentId, isLoading: locLoading } = useCurrentLocation();
  const configsQ = useConfigs(currentId ?? undefined);
  const unonboarded =
    !locLoading && !configsQ.isLoading && (configsQ.data?.length ?? 0) === 0;

  // A normal (non-admin) user whose location has no formats yet is walked through the
  // guided onboarding wizard instead of landing on an empty Generate screen (6.2/6.3).
  // Admins keep the home page (with a first-run import nudge) so they can also reach
  // the broader setup surfaces.
  const redirectToWelcome = !isAdmin && unonboarded;

  // First-run nudge for admins: point them at the migration/import flow to bring
  // their legacy EDD Builder formats over (P1-C).
  const showFirstRun = isAdmin && unonboarded;

  if (redirectToWelcome) {
    return <Navigate to="/welcome" replace />;
  }

  return (
    <div className="page stack" style={{ gap: "var(--space-6)" }}>
      <div>
        <h1 style={{ marginBottom: 4 }}>{t("home.greeting", { name })}</h1>
        <p className="muted" style={{ margin: 0 }}>
          {isAdmin ? t("home.adminSubtitle") : t("home.techSubtitle")}
        </p>
      </div>

      {showFirstRun && (
        <div
          className="card stack"
          style={{ gap: "var(--space-3)", borderColor: "var(--color-primary)" }}
        >
          <h2 style={{ margin: 0 }}>{t("home.firstRunTitle")}</h2>
          <p className="muted" style={{ margin: 0, maxWidth: 620 }}>
            {t("home.firstRunBody")}
          </p>
          <div>
            <Button variant="primary" onClick={() => navigate("/setup")}>
              {t("home.firstRunCta")}
            </Button>
          </div>
        </div>
      )}

      {/* Hero: generate-first — the one thing every user does (Phase 6.3). */}
      <div
        className="card stack"
        style={{ gap: "var(--space-3)", background: "var(--color-primary-soft)", borderColor: "transparent" }}
      >
        <h2 style={{ margin: 0 }}>{t("home.generateTitle")}</h2>
        <p className="muted" style={{ margin: 0, maxWidth: 560 }}>
          {t("home.createBody")}
        </p>
        <div>
          <Button variant="primary" onClick={() => navigate("/new")}>
            {t("home.generateCta")}
          </Button>
        </div>
      </div>

      <section className="stack">
        <h2 style={{ margin: 0 }}>{t("home.recentTitle")}</h2>
        <RecentDeliverables />
      </section>

      {isAdmin && (
        <section className="stack">
          <h2 style={{ margin: 0 }}>{t("home.adminToolsTitle")}</h2>
          <div className="grid-cards">
            <AdminCard to="/configs" title={t("home.cardFormatsTitle")} body={t("home.cardFormatsBody")} />
            <AdminCard to="/specs" title={t("home.cardStandardsTitle")} body={t("home.cardStandardsBody")} />
            <AdminCard to="/admin" title={t("home.cardTeamTitle")} body={t("home.cardTeamBody")} />
          </div>
        </section>
      )}
    </div>
  );
}
